import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { FaShieldAlt, FaFileContract, FaUsers, FaGavel, FaClipboardCheck, FaHandshake } from 'react-icons/fa';

const TrusteeRegistrarServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', link: '/services' },
    { label: 'Trustee And Registrar Services' }
  ];

  const services = [
    {
      icon: <FaShieldAlt className="w-8 h-8 text-primary" />,
      title: "Corporate Trustee Services",
      description: "Professional trustee services for corporate entities, pension funds, and institutional trusts."
    },
    {
      icon: <FaFileContract className="w-8 h-8 text-primary" />,
      title: "Fund Administration",
      description: "Comprehensive fund administration including NAV calculation, investor reporting, and compliance."
    },
    {
      icon: <FaUsers className="w-8 h-8 text-primary" />,
      title: "Registrar Services",
      description: "Complete registrar and transfer agent services for investment funds and corporate securities."
    },
    {
      icon: <FaGavel className="w-8 h-8 text-primary" />,
      title: "Fiduciary Services",
      description: "Professional fiduciary services ensuring compliance with regulatory requirements and best practices."
    },
    {
      icon: <FaClipboardCheck className="w-8 h-8 text-primary" />,
      title: "Compliance Monitoring",
      description: "Ongoing compliance monitoring and reporting to ensure adherence to regulatory standards."
    },
    {
      icon: <FaHandshake className="w-8 h-8 text-primary" />,
      title: "Custodial Services",
      description: "Secure custodial services for assets with segregated accounts and comprehensive reporting."
    }
  ];

  const benefits = [
    "Experienced team with deep knowledge of fiduciary responsibilities",
    "Robust compliance framework ensuring regulatory adherence",
    "Advanced technology platforms for efficient administration",
    "Transparent reporting and real-time account monitoring",
    "Professional indemnity insurance and bonding protection",
    "Segregated client assets with independent custody arrangements",
    "Regular audit and review processes by independent auditors",
    "Dedicated relationship management and client support"
  ];

  const processSteps = [
    {
      step: "01",
      title: "Initial Consultation",
      description: "Comprehensive assessment of your trustee and registrar service requirements."
    },
    {
      step: "02",
      title: "Service Design",
      description: "Customized service structure tailored to your specific operational needs."
    },
    {
      step: "03",
      title: "Implementation",
      description: "Seamless onboarding with full system integration and staff training."
    },
    {
      step: "04",
      title: "Ongoing Service",
      description: "Continuous professional service delivery with regular review and optimization."
    }
  ];

  const expertise = [
    {
      title: "Investment Funds",
      description: "Mutual funds, hedge funds, private equity, and alternative investment vehicles"
    },
    {
      title: "Pension Plans",
      description: "Corporate pension schemes, retirement plans, and employee benefit trusts"
    },
    {
      title: "Corporate Trusts",
      description: "Bond trusts, security trusts, and corporate restructuring vehicles"
    },
    {
      title: "Family Trusts",
      description: "Private wealth management trusts and estate planning structures"
    }
  ];

  return (
    <>
      <PageHeader 
        title="Trustee And Registrar Services" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/unrecognizable-businessman-pointing-printed-chart-showing-graph-colleagues-professional-content-partners-making-notes-statistics-cooperation-communication-partnership-concept_74855-14267.jpg_k9kjwo.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-heading font-bold text-primary mb-6">
                Professional Fiduciary Excellence
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                <span className="font-semibold">Blockfirst Capital</span> provides comprehensive trustee and registrar services 
                with the highest standards of fiduciary responsibility. Our experienced team delivers 
                professional administration, compliance oversight, and custodial services for a wide 
                range of institutional and private clients.
              </p>
              <p className="text-lg text-gray-700">
                With robust governance frameworks, advanced technology systems, and deep regulatory 
                expertise, we ensure your trust and fund administration needs are met with precision 
                and professional integrity.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/business-team-working-together.jpg" 
                alt="Trustee Services" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>

          {/* Services Grid */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-4">Our Services</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Comprehensive trustee and registrar solutions designed to meet the complex needs of 
                institutional clients and private wealth management.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300"
                >
                  <div className="bg-gray-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Expertise Areas */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-4">Areas of Expertise</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Specialized knowledge across diverse trust structures and investment vehicles.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {expertise.map((area, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-8 shadow-custom"
                >
                  <h3 className="text-2xl font-heading font-semibold mb-4 text-primary">{area.title}</h3>
                  <p className="text-gray-600 text-lg">{area.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Process Steps */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-4">Our Process</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                A structured approach to delivering trustee and registrar services with professional excellence.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="bg-gray-900 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-6 mx-auto">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/workmates-with-statistic-documents.jpg" 
                alt="Compliance & Audit" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-heading font-bold text-primary mb-6">Why Choose Our Services?</h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start"
                  >
                                    <div className="bg-gray-100 rounded-full p-2 mr-4 mt-1">
                  <div className="w-2 h-2 bg-gray-700 rounded-full"></div>
                    </div>
                    <p className="text-gray-700 text-lg">{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="bg-primary text-white rounded-2xl p-8 md:p-12"
          >
            <div className="text-center">
              <h2 className="text-3xl font-heading font-bold mb-6">Partner with Trusted Professionals</h2>
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Discover how our trustee and registrar services can provide the professional fiduciary 
                support your organization needs with expertise, integrity, and compliance excellence.
              </p>
              <a 
                href="/contact-us" 
                className="btn btn-secondary text-lg py-4 px-8 shadow-xl hover:shadow-secondary/20 btn-3d inline-block"
              >
                Discuss Your Requirements
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default TrusteeRegistrarServicesPage; 